<!-- Overlay -->
<div
  class="fixed inset-0 z-50 flex items-center justify-center bg-navy-900/60 backdrop-blur-sm p-4"
  (click)="closed.emit()"
>
  <div
    class="bg-white rounded-2xl shadow-xl w-full max-w-lg flex flex-col overflow-hidden"
    (click)="$event.stopPropagation()"
  >
    <!-- Header -->
    <div class="flex items-start justify-between px-6 pt-6 pb-4">
      <h2 class="text-base font-bold text-navy-900">New Investigation</h2>
      <button
        type="button"
        (click)="closed.emit()"
        class="w-8 h-8 flex items-center justify-center rounded-full border border-surface-300 hover:bg-surface-100 transition-colors cursor-pointer shrink-0"
      >
        <img src="/icons/close-icon.svg" class="w-3.5 h-3.5" alt="Close" />
      </button>
    </div>

    <!-- Body -->
    <div class="px-6 pb-2 flex flex-col gap-4 max-h-[70vh] overflow-y-auto">
      <!-- Automatic Linking info (from incident detail) -->
      @if (isLinked()) {
        <div class="flex items-start gap-3 bg-blue-50 border border-blue-200 rounded-xl px-4 py-3">
          <img src="/icons/info-icon.svg" class="w-4 h-4 shrink-0 mt-0.5 opacity-60" alt="" />
          <div>
            <p class="text-xs font-semibold text-blue-800 mb-0.5">Automatic Linking</p>
            <p class="text-xs text-blue-700">
              This investigation will be automatically linked to incident
              <span class="font-semibold">{{ linkedIncidentLabel() ?? linkedIncidentId() }}</span
              >.
            </p>
          </div>
        </div>
      } @else {
        <!-- Incident select (from investigation feed) -->
        <div class="flex flex-col gap-1.5">
          <label class="text-xs font-semibold text-navy-900">
            Linked Incident <span class="text-red-500">*</span>
          </label>
          <app-select
            [options]="incidentOptions()"
            [value]="selectedIncidentId()"
            placeholder="Select incident…"
            [loading]="incidentsLoading()"
            [hasMore]="incidentsHasMore()"
            [searchable]="true"
            [error]="showError('incident')"
            (changed)="selectedIncidentId.set($event)"
            (loadMore)="onLoadMoreIncidents()"
          />
          @if (showError('incident')) {
            <p class="text-xs text-red-500">Required</p>
          }
        </div>
      }

      <!-- Two-column grid for the rest of the fields -->
      <div class="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <!-- Date of Notice -->
        <div class="flex flex-col gap-1.5">
          <label class="text-xs font-semibold text-navy-900">
            Date of Notice <span class="text-red-500">*</span>
          </label>
          <app-date-picker
            [value]="investigationNoticeDate()"
            placeholder="Select date…"
            [error]="showError('noticeDate')"
            (valueChange)="investigationNoticeDate.set($event)"
          />
          @if (showError('noticeDate')) {
            <p class="text-xs text-red-500">Required</p>
          }
        </div>

        <!-- Person Sending Notice -->
        <div class="flex flex-col gap-1.5">
          <label class="text-xs font-semibold text-navy-900">
            Person Sending Notice <span class="text-red-500">*</span>
          </label>
          <app-person-select
            [value]="personSendingNoticeId()"
            [error]="showError('person')"
            (changed)="personSendingNoticeId.set($event)"
          />
          @if (showError('person')) {
            <p class="text-xs text-red-500">Required</p>
          }
        </div>

        <!-- Investigating Program -->
        <div class="flex flex-col gap-1.5">
          <label class="text-xs font-semibold text-navy-900">
            Investigating Program <span class="text-red-500">*</span>
          </label>
          <app-select
            [options]="programOptions()"
            [value]="investigatingProgramId()"
            placeholder="Select program…"
            [loading]="programsLoading()"
            [hasMore]="programsHasMore()"
            [error]="showError('program')"
            (changed)="investigatingProgramId.set($event)"
            (loadMore)="onLoadMorePrograms()"
          />
          @if (showError('program')) {
            <p class="text-xs text-red-500">Required</p>
          }
        </div>
      </div>

      <!-- Categorizations — full width -->
      <div class="flex flex-col gap-1.5">
        <label class="text-xs font-semibold text-navy-900">
          Allegation Categories <span class="text-red-500">*</span>
        </label>
        <app-multiselect-dropdown
          [options]="categoryOptions()"
          [selected]="investigationCategorizations()"
          [error]="showError('categories')"
          (selectedChange)="investigationCategorizations.set($event)"
          placeholder="Select categories…"
        />
        @if (showError('categories')) {
          <p class="text-xs text-red-500">Select at least one category</p>
        }
      </div>
    </div>

    <!-- Footer -->
    <div class="px-6 py-4 border-t border-surface-200 flex items-center justify-end gap-3 mt-2">
      <button
        type="button"
        (click)="closed.emit()"
        class="px-5 py-2.5 text-sm font-semibold text-slate-500 hover:text-navy-900 transition-colors cursor-pointer"
      >
        Cancel
      </button>
      <button
        type="button"
        (click)="onSubmit()"
        [disabled]="saving()"
        class="px-5 py-2.5 text-sm font-semibold text-white bg-accent-500 hover:opacity-90 disabled:opacity-50 rounded-xl transition-opacity cursor-pointer disabled:cursor-not-allowed"
      >
        {{ saving() ? 'Creating…' : 'Create Investigation' }}
      </button>
    </div>
  </div>
</div>
